import _ from 'lodash';
import { createSelector } from 'reselect';

import { HousingType, LoanType } from 'constants/loanApplication';
import { PendingChangesTypeToHistoryTypeMap } from 'constants/pendingChanges';
import { getConditionalArray, getConditionalObject } from 'utils/data';
import {
  checkIsPositiveRentalHistoryVisible,
  getAddressData,
  getBorrowerFullName,
} from 'utils/loanApplication';
import {
  getApplicableAddressFieldsPendingChanges,
  getNewAndDeletedEntities,
} from 'utils/pendingChanges';

import { selectLoanApplication, selectLoanApplicationBorrowers } from './loanApplication.selectors';
import { selectLoanApplicationSubjectProperty } from './loanApplicationProperties.selectors';
import { selectEntitiesChangedPropsByIds } from './loanChangesHistory.selectors';
import { selectResidenceValues } from './residences.selectors';

const selectBorrowerCrmContactsMap = ({ loanApplication }) =>
  loanApplication.loanApplicationBorrowerCrmContacts;

const selectBorrowerPendingContactLinksMap = ({ loanApplication }) =>
  loanApplication.loanApplicationBorrowerPendingContactLinks;

const selectBorrowerIdArg = (_state, borrowerId) => borrowerId;

export const selectBorrowerCrmContactById = createSelector(
  [selectBorrowerCrmContactsMap, selectBorrowerIdArg],
  (contactsMap, borrowerId) => contactsMap?.[borrowerId] ?? null,
);

export const selectBorrowerPendingContactLinkById = createSelector(
  [selectBorrowerPendingContactLinksMap, selectBorrowerIdArg],
  (pendingMap, borrowerId) => pendingMap?.[borrowerId] ?? null,
);

export const selectBorrowersTableData = createSelector(
  selectLoanApplicationBorrowers,
  loanApplicationBorrowers =>
    loanApplicationBorrowers?.map(borrower => ({
      ...borrower,
      fullName: getBorrowerFullName(borrower.borrowerFullName),
    })),
);

export const selectOrderedBorrowersTableData = createSelector(
  selectBorrowersTableData,
  borrowersTableData => {
    return borrowersTableData && _.orderBy(borrowersTableData, ['orderNumber'], ['asc']);
  },
);

export const selectDeletedAndNewBorrowers = createSelector(
  selectBorrowersTableData,
  borrowersData => {
    const { deletedEntities, newEntities } = getNewAndDeletedEntities(borrowersData || []);

    return {
      deletedBorrowers: deletedEntities,
      newBorrowers: newEntities,
    };
  },
);

export const selectBorrowersPendingChanges = createSelector(
  [
    selectBorrowersTableData,
    selectResidenceValues,
    selectLoanApplicationSubjectProperty,
    state => state,
  ],
  (borrowers, residences, subjectProperty, state) => {
    const { borrowersIds, recordsWithOuterPendingChanges } = (borrowers ?? []).reduce(
      (acc, borrowerData) => {
        const {
          id: borrowerId,
          addressId,
          previousAddressesIds,
          mailingAddressId,
          isFirstTimeHomeBuyer,
          modifiedFields,
          pendingChangesActionType,
        } = borrowerData;
        const subjectPropertyAddressId = subjectProperty?.address.id;

        const borrowerResidences = residences.filter(
          residence => residence.borrowerId === borrowerId,
        );
        const residencesMapByAddressId = _.keyBy(borrowerResidences, 'addressId');

        const currentResidenceId = residencesMapByAddressId[addressId]?.residenceId;
        const previousAddressesResidencesIds = previousAddressesIds?.map(
          id => residencesMapByAddressId[id]?.residenceId,
        );

        acc.borrowersIds.push(borrowerId);

        if (modifiedFields.includes('IsFirstTimeHomeBuyer')) {
          _.set(acc, `recordsWithOuterPendingChanges[${borrowerId}].isFirstTimeHomeBuyer`, {
            initialValue: !isFirstTimeHomeBuyer,
            newValue: isFirstTimeHomeBuyer,
            actionType: PendingChangesTypeToHistoryTypeMap[pendingChangesActionType],
          });
        }

        if (addressId || previousAddressesIds || mailingAddressId || subjectPropertyAddressId) {
          const addressesPendingChanges = selectEntitiesChangedPropsByIds([
            ...getConditionalArray(addressId, [addressId, currentResidenceId]),
            ...getConditionalArray(subjectPropertyAddressId, [subjectPropertyAddressId]),
            ...getConditionalArray(previousAddressesIds, [
              ...(previousAddressesIds ?? []),
              ...(previousAddressesResidencesIds ?? []),
            ]),
            ...getConditionalArray(mailingAddressId, [mailingAddressId]),
          ])(state);

          const {
            [addressId]: currentAddressPendingChanges,
            [currentResidenceId]: currentResidencePendingChanges,
            [mailingAddressId]: mailingAddressPendingChanges,
            [subjectPropertyAddressId]: subjectPropertyAddressPendingChanged,
          } = addressesPendingChanges;

          if (currentAddressPendingChanges || currentResidencePendingChanges) {
            _.set(acc, `recordsWithOuterPendingChanges[${borrowerId}].address`, {
              ...getConditionalObject(currentAddressPendingChanges, () =>
                getApplicableAddressFieldsPendingChanges(currentAddressPendingChanges ?? {}),
              ),
              ...getConditionalObject(
                currentResidencePendingChanges,
                currentResidencePendingChanges,
              ),
            });
          }

          if (subjectPropertyAddressPendingChanged) {
            _.set(acc, `recordsWithOuterPendingChanges[${borrowerId}].subjectProperty`, {
              ...getConditionalObject(subjectPropertyAddressPendingChanged, () =>
                getApplicableAddressFieldsPendingChanges(
                  subjectPropertyAddressPendingChanged ?? {},
                ),
              ),
            });
          }

          if (mailingAddressPendingChanges) {
            _.set(
              acc,
              `recordsWithOuterPendingChanges[${borrowerId}].mailingAddress`,
              getApplicableAddressFieldsPendingChanges(mailingAddressPendingChanges),
            );
          }

          previousAddressesIds?.forEach(prevAddressId => {
            const prevAddressPendingChanges = addressesPendingChanges[prevAddressId];
            const prevAddressResidencePendingChanges =
              addressesPendingChanges[residencesMapByAddressId[prevAddressId]?.residenceId];

            if (prevAddressPendingChanges || prevAddressResidencePendingChanges) {
              _.set(
                acc,
                `recordsWithOuterPendingChanges[${borrowerId}].previousAddresses[${prevAddressId}]`,
                {
                  isSeparateChange: true,
                  ...getConditionalObject(prevAddressPendingChanges, () =>
                    getApplicableAddressFieldsPendingChanges(prevAddressPendingChanges ?? {}),
                  ),
                  ...getConditionalObject(
                    prevAddressResidencePendingChanges,
                    prevAddressResidencePendingChanges,
                  ),
                },
              );
            }
          });
        }

        return acc;
      },
      { borrowersIds: [], recordsWithOuterPendingChanges: {} },
    );

    const borrowersPendingChanges = selectEntitiesChangedPropsByIds(borrowersIds)(state);

    return _.merge(borrowersPendingChanges, recordsWithOuterPendingChanges);
  },
);

export const selectSubjectPropertyForResidence = createSelector(
  selectLoanApplicationSubjectProperty,
  subjectProperty => {
    if (!subjectProperty) {
      return null;
    }

    const { address: subjectPropertyAddress, id: subjectPropertyAddressId } =
      subjectProperty.address;

    return {
      ...getAddressData({ ...subjectPropertyAddress, addressId: subjectPropertyAddressId }),
      isSubjectProperty: true,
      housingType: HousingType.OWN,
    };
  },
);

export const selectBorrowerFieldsVisibilityBySideData = createSelector(
  selectLoanApplication,
  ({ common }) => {
    const { loanPurpose, loanType } = common;

    const isGovernmentLoan = [LoanType.FHA, LoanType.VA, LoanType.USDA].includes(loanType);

    return {
      positiveRentalHistory: checkIsPositiveRentalHistoryVisible(loanType, loanPurpose),
      caivrsNumber: isGovernmentLoan,
      caivrsAuthorizationCode: isGovernmentLoan,
    };
  },
);
